import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Statistic,
  Popconfirm,
  Tabs
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  TrophyOutlined,
  DollarOutlined,
  ShopOutlined,
  FilePdfOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import { stationOwnerAPI } from '../services/api';
import { handleExport } from '../utils/exportUtils';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

interface Customer {
  _id: string;
  firstName?: string;
  lastName?: string;
  phone: string;
  email?: string;
  customerType: string;
  companyName?: string;
  loyaltyPoints: number;
  loyaltyTier: string;
  totalSpent: number;
  totalVisits: number;
  lastVisit?: string;
  preferredStation?: {
    _id: string;
    name: string;
  };
}

const Customers: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [filters, setFilters] = useState<any>({
    stationId: 'all',
    customerType: 'all',
    loyaltyTier: 'all',
    search: ''
  });
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchStations();
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.stationId !== 'all') params.stationId = filters.stationId;
      if (filters.customerType !== 'all') params.customerType = filters.customerType;
      if (filters.loyaltyTier !== 'all') params.loyaltyTier = filters.loyaltyTier;
      if (filters.search) params.search = filters.search;

      const response = await stationOwnerAPI.getCustomers(params);
      setCustomers(response);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Müşteriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = () => {
    setSelectedCustomer(null);
    form.resetFields();
    form.setFieldsValue({
      customerType: 'individual',
      loyaltyTier: 'bronze'
    });
    setModalVisible(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    form.setFieldsValue({
      firstName: customer.firstName,
      lastName: customer.lastName,
      phone: customer.phone,
      email: customer.email,
      customerType: customer.customerType,
      companyName: customer.companyName,
      stationId: customer.preferredStation?._id
    });
    setModalVisible(true);
  };

  const handleViewDetail = async (customer: Customer) => {
    try {
      const response = await stationOwnerAPI.getCustomerDetail(customer._id);
      setSelectedCustomer(response.customer);
      setDetailModalVisible(true);
    } catch (error: any) {
      message.error('Müşteri detayı yüklenemedi');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        email: values.email,
        customerType: values.customerType,
        companyName: values.companyName,
        stationId: values.stationId
      };

      if (selectedCustomer) {
        await stationOwnerAPI.updateCustomer(selectedCustomer._id, data);
        message.success('Müşteri güncellendi');
      } else {
        await stationOwnerAPI.createCustomer(data);
        message.success('Müşteri eklendi');
      }

      setModalVisible(false);
      form.resetFields();
      setSelectedCustomer(null);
      fetchCustomers();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await stationOwnerAPI.deleteCustomer(id);
      message.success('Müşteri silindi');
      fetchCustomers();
    } catch (error: any) {
      message.error('İşlem başarısız');
    }
  };

  const loyaltyTierColors: any = {
    platinum: 'purple',
    gold: 'gold',
    silver: 'default',
    bronze: 'orange'
  };

  const loyaltyTierLabels: any = {
    platinum: 'Platin',
    gold: 'Altın',
    silver: 'Gümüş',
    bronze: 'Bronz'
  };

  const columns = [
    {
      title: 'Müşteri',
      key: 'name',
      render: (record: Customer) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {record.customerType === 'corporate' && record.companyName
              ? record.companyName
              : `${record.firstName || ''} ${record.lastName || ''}`.trim() || 'İsimsiz'}
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>{record.phone}</div>
        </div>
      )
    },
    {
      title: 'Tip',
      dataIndex: 'customerType',
      key: 'customerType',
      render: (type: string) => {
        const labels: any = {
          individual: 'Bireysel',
          corporate: 'Kurumsal',
          fleet: 'Filolu'
        };
        return <Tag>{labels[type] || type}</Tag>;
      }
    },
    {
      title: 'Sadakat Seviyesi',
      dataIndex: 'loyaltyTier',
      key: 'loyaltyTier',
      render: (tier: string, record: Customer) => (
        <Space>
          <Tag color={loyaltyTierColors[tier]}>
            {loyaltyTierLabels[tier]} ({record.loyaltyPoints} puan)
          </Tag>
        </Space>
      )
    },
    {
      title: 'Toplam Harcama',
      dataIndex: 'totalSpent',
      key: 'totalSpent',
      render: (amount: number) => (
        <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
          {amount.toLocaleString('tr-TR')} ₺
        </span>
      )
    },
    {
      title: 'Ziyaret',
      dataIndex: 'totalVisits',
      key: 'totalVisits',
      render: (visits: number) => `${visits} kez`
    },
    {
      title: 'Son Ziyaret',
      dataIndex: 'lastVisit',
      key: 'lastVisit',
      render: (date: string) => date ? dayjs(date).format('DD MMM YYYY') : '-'
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 200,
      render: (_: any, record: Customer) => (
        <Space>
          <Button type="link" onClick={() => handleViewDetail(record)}>
            Detay
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditCustomer(record)}
          />
          <Popconfirm
            title="Bu müşteriyi silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const totalCustomers = customers.length;
  const totalSpent = customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0);
  const totalVisits = customers.reduce((sum, c) => sum + (c.totalVisits || 0), 0);
  const platinumCustomers = customers.filter(c => c.loyaltyTier === 'platinum').length;

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12}>
          <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>
            <UserOutlined /> Müşteri Yönetimi
          </Title>
        </Col>
        <Col xs={24} md={12}>
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto', float: isMobile ? 'none' : 'right' }} wrap>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleAddCustomer} 
              block={isMobile}
            >
              Yeni Müşteri Ekle
            </Button>
            <Button 
              icon={<FilePdfOutlined />}
              onClick={() => handleExport(
                () => stationOwnerAPI.exportCustomersPDF(),
                `musteriler_${Date.now()}.pdf`,
                () => message.success('PDF başarıyla indirildi')
              )}
              block={isMobile}
            >
              PDF
            </Button>
            <Button 
              icon={<FileExcelOutlined />}
              onClick={() => handleExport(
                () => stationOwnerAPI.exportCustomersExcel(),
                `musteriler_${Date.now()}.xlsx`,
                () => message.success('Excel başarıyla indirildi')
              )}
              block={isMobile}
            >
              Excel
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Özet İstatistikler */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Müşteri"
              value={totalCustomers}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Harcama"
              value={totalSpent}
              prefix={<DollarOutlined />}
              suffix="₺"
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Ziyaret"
              value={totalVisits}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Platin Müşteriler"
              value={platinumCustomers}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtreler */}
      <Card style={{ marginBottom: 24 }} styles={{ body: { padding: isMobile ? '12px' : '16px' } }}>
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Ara (isim, telefon, email)"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              allowClear
              size={isMobile ? 'small' : 'middle'}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              value={filters.stationId}
              onChange={(value) => setFilters({ ...filters, stationId: value })}
              style={{ width: '100%' }}
              placeholder="İstasyon"
              size={isMobile ? 'small' : 'middle'}
            >
              <Option value="all">Tüm İstasyonlar</Option>
              {stations.map((station: any) => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              value={filters.customerType}
              onChange={(value) => setFilters({ ...filters, customerType: value })}
              style={{ width: '100%' }}
              placeholder="Müşteri Tipi"
              size={isMobile ? 'small' : 'middle'}
            >
              <Option value="all">Tümü</Option>
              <Option value="individual">Bireysel</Option>
              <Option value="corporate">Kurumsal</Option>
              <Option value="fleet">Filolu</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              value={filters.loyaltyTier}
              onChange={(value) => setFilters({ ...filters, loyaltyTier: value })}
              style={{ width: '100%' }}
              placeholder="Sadakat Seviyesi"
              size={isMobile ? 'small' : 'middle'}
            >
              <Option value="all">Tümü</Option>
              <Option value="platinum">Platin</Option>
              <Option value="gold">Altın</Option>
              <Option value="silver">Gümüş</Option>
              <Option value="bronze">Bronz</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      <Card styles={{ body: { padding: isMobile ? '8px' : '24px' } }}>
        <Table
          columns={columns}
          dataSource={customers}
          rowKey="_id"
          loading={loading}
          pagination={{ 
            pageSize: 10,
            showSizeChanger: false,
            responsive: true
          }}
          scroll={{ x: 'max-content', y: isMobile ? '60vh' : undefined }}
          size={isMobile ? 'small' : 'middle'}
        />
      </Card>

      {/* Müşteri Ekleme/Düzenleme Modal */}
      <Modal
        title={selectedCustomer ? 'Müşteri Düzenle' : 'Yeni Müşteri Ekle'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedCustomer(null);
          form.resetFields();
        }}
        footer={null}
        width={isMobile ? '95%' : 600}
        styles={{ body: { padding: isMobile ? '16px' : '24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="customerType"
            label="Müşteri Tipi"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="individual">Bireysel</Option>
              <Option value="corporate">Kurumsal</Option>
              <Option value="fleet">Filolu</Option>
            </Select>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.customerType !== currentValues.customerType}>
            {({ getFieldValue }) => {
              const customerType = getFieldValue('customerType');
              
              if (customerType === 'corporate') {
                return (
                  <Form.Item
                    name="companyName"
                    label="Şirket Adı"
                    rules={[{ required: true, message: 'Şirket adı giriniz' }]}
                  >
                    <Input />
                  </Form.Item>
                );
              }
              
              return (
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="firstName" label="Ad">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="lastName" label="Soyad">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              );
            }}
          </Form.Item>

          <Form.Item
            name="phone"
            label="Telefon"
            rules={[{ required: true, message: 'Telefon giriniz' }]}
          >
            <Input placeholder="05551234567" />
          </Form.Item>

          <Form.Item
            name="email"
            label="E-posta"
          >
            <Input type="email" />
          </Form.Item>

          <Form.Item
            name="stationId"
            label="Tercih Edilen İstasyon"
          >
            <Select placeholder="İstasyon seçin (opsiyonel)" allowClear>
              {stations.map((station: any) => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedCustomer ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setSelectedCustomer(null);
                form.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Müşteri Detay Modal */}
      <Modal
        title="Müşteri Detayları"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
        styles={{ body: { padding: '24px' } }}
      >
        {selectedCustomer && (
          <Tabs
            items={[
              {
                key: 'info',
                label: 'Genel Bilgiler',
                children: (
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <strong>Ad Soyad:</strong> {selectedCustomer.firstName} {selectedCustomer.lastName}
                    </Col>
                    <Col xs={24} sm={12}>
                      <strong>Telefon:</strong> {selectedCustomer.phone}
                    </Col>
                    <Col xs={24} sm={12}>
                      <strong>E-posta:</strong> {selectedCustomer.email || '-'}
                    </Col>
                    <Col xs={24} sm={12}>
                      <strong>Sadakat Seviyesi:</strong>{' '}
                      <Tag color={loyaltyTierColors[selectedCustomer.loyaltyTier]}>
                        {loyaltyTierLabels[selectedCustomer.loyaltyTier]}
                      </Tag>
                    </Col>
                    <Col xs={24} sm={12}>
                      <strong>Sadakat Puanı:</strong> {selectedCustomer.loyaltyPoints}
                    </Col>
                    <Col xs={24} sm={12}>
                      <strong>Toplam Harcama:</strong> {selectedCustomer.totalSpent.toLocaleString('tr-TR')} ₺
                    </Col>
                    <Col xs={24} sm={12}>
                      <strong>Toplam Ziyaret:</strong> {selectedCustomer.totalVisits}
                    </Col>
                    <Col xs={24} sm={12}>
                      <strong>Son Ziyaret:</strong>{' '}
                      {selectedCustomer.lastVisit ? dayjs(selectedCustomer.lastVisit).format('DD MMMM YYYY') : '-'}
                    </Col>
                  </Row>
                )
              }
            ]}
          />
        )}
      </Modal>
    </div>
  );
};

export default Customers;

